import { DateTime } from "luxon";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  Mail02Icon,
  MailRemove02Icon,
  MailSend02Icon,
  SentIcon,
  UserEdit01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Banner } from "shared/components/Banner";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { formatPhone } from "shared/utils";
import { ApplicationStatus } from "shared/types";
import { employerDeclineReasonLabel } from "shared/app/EmployerVerification/declineReasons";
import { landlordSurveyApi } from "api/landlord-surveys/landlord-surveys";
import { getErrorMessage } from "utils/error";
import { useCombinedIncomeContext } from "views/applications-id-report/slices/CombinedIncomeReportSlice/providers/useCombinedIncome";
import {
  EmployerVerification,
  EmployerVerificationActivity,
  EmployerVerificationAnswer
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { EditSupervisorDetailsModal } from "./EditSupervisorDetailsModal";

/**
 * BSCPF-6584. The employer's side of an employment income.
 *
 * Laid out to match the housing history premium survey (SurveyResults +
 * PremiumSurveyActivityTimeline) so the two halves of the shared survey engine read the
 * same way on the report.
 *
 * Resend and Edit details both go through the shared token route, which dispatches on the
 * survey's kind. Both ask for confirmation first, per the design: a resend texts a third
 * party, and an edit can clear answers already given.
 *
 * Mismatches are computed by the API, not here — the banner's count and the tab's badge
 * read the same `mismatch_count`, so they cannot disagree.
 */

const ACTIVITY_ICON = {
  created: Mail02Icon,
  sent: SentIcon,
  edited: UserEdit01Icon,
  opened: Clock01Icon,
  completed: CheckmarkCircle02Icon,
  declined: MailRemove02Icon,
  no_response: Clock01Icon
} as const satisfies Record<EmployerVerificationActivity["kind"], unknown>;

/** Statuses in which the survey can still be answered or re-sent. */
const OPEN_STATUSES: EmployerVerification["status"][] = [
  "pending",
  "started",
  "no_response"
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const US_DATE = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

/** Mirrors SurveyResults' formatter so both surveys render answers identically. */
const formatResponse = (value: string) => {
  if (value === "true") return "Yes";
  if (value === "false") return "No";
  if (ISO_DATE.test(value))
    return DateTime.fromISO(value).toFormat("LLL dd, yyyy");
  if (US_DATE.test(value))
    return DateTime.fromFormat(value, "M/d/yyyy").toFormat("LLL dd, yyyy");

  return value;
};

/**
 * Why Resend and Edit details are unavailable, in the order a PM would need to hear it:
 * a decline is final, an on-hold application is the PM's own doing, and anything else
 * that is no longer open has its own reason. `status` is only passed where that last
 * group matters, so Resend and Edit share this without sharing their differences.
 *
 * It reads the status rather than a bare "is it open" flag because three different
 * things close a survey and only one of them is the employer answering: telling a PM
 * "the employer has already responded" about a survey that was canceled, or that never
 * reached anyone, sends them looking for an answer that does not exist.
 */
const CLOSED_REASON: Partial<
  Record<NonNullable<EmployerVerification["status"]>, string>
> = {
  completed: "The employer has already responded",
  canceled: "This verification was canceled",
  failed: "This verification could not be delivered"
};

const blockedReason = ({
  isDeclined,
  isOnHold,
  status
}: {
  isDeclined: boolean;
  isOnHold: boolean;
  status?: EmployerVerification["status"];
}): string | undefined => {
  if (isDeclined) return "Cannot resend a verification the employer declined";
  if (isOnHold) return "Resume the application to resend the verification";
  if (status && CLOSED_REASON[status]) return CLOSED_REASON[status];

  return undefined;
};

const supervisorName = (supervisor: EmployerVerification["supervisor"]) => {
  const name = [supervisor.first_name, supervisor.last_name]
    .filter(Boolean)
    .join(" ");

  return name || undefined;
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-sm font-bold text-content-1000 pb-3 border-b border-alpha-80">
    {children}
  </h2>
);

const DetailRow = ({
  label,
  value
}: {
  label: string;
  value?: string | null;
}) => {
  if (!value) return null;

  return (
    <div className="flex gap-2 justify-between border-b border-alpha-40 py-2 text-sm font-medium">
      <p className="text-content-300">{label}</p>
      <p className="text-content-1000 text-right">{value}</p>
    </div>
  );
};

/**
 * One answer. A mismatch is tinted and carries the applicant's own figure underneath, so
 * the contradiction is readable without cross-referencing the Self-reported tab.
 */
const AnswerRow = ({ answer }: { answer: EmployerVerificationAnswer }) => (
  <div
    className={`flex gap-2 justify-between border-b border-alpha-40 py-2 text-sm font-medium ${
      answer.mismatch ? "bg-warning-50/60 -mx-2 px-2 rounded" : ""
    }`}
  >
    <p className="text-content-300">{answer.question}</p>
    <div className="text-right">
      <p
        className={
          answer.mismatch ? "text-warning-800 font-bold" : "text-content-1000"
        }
      >
        {answer.answer ? formatResponse(answer.answer) : "-"}
      </p>
      {answer.mismatch && answer.reported ? (
        <p className="mt-1 text-xs text-content-400">
          Applicant reported: {answer.reported}
        </p>
      ) : null}
    </div>
  </div>
);

const ActivityTimeline = ({
  activity
}: {
  activity: EmployerVerificationActivity[];
}) => {
  if (activity.length === 0) return null;

  const events = activity.slice().reverse();

  return (
    <div className="mt-8">
      <SectionHeading>Activity</SectionHeading>

      <div className="relative pt-4">
        <ul className="relative text-content-1000 text-sm">
          {events.map((entry, i) => {
            const dt = DateTime.fromISO(entry.occurred_at);
            const isLast = i === events.length - 1;

            return (
              <li
                key={`${entry.kind}-${entry.occurred_at}-${i}`}
                className="grid grid-cols-[100px_44px_minmax(0,1fr)] gap-3 pb-8 last:pb-4 sm:grid-cols-[90px_44px_minmax(0,1fr)]"
              >
                <div className="flex w-fit flex-col items-start mt-1">
                  <p className="text-xs font-bold whitespace-nowrap">
                    {dt.toFormat("MMM d, yyyy")}
                  </p>
                  <p className="text-xs text-content-400 self-end mt-1">
                    {dt.toFormat("h:mm a")}
                  </p>
                </div>

                <div className="relative flex h-full min-h-10 flex-col items-center">
                  <div
                    className={`absolute left-1/2 w-px -translate-x-1/2 border-l border-dashed border-content-100 ${
                      i === 0 ? "top-[-1rem] bottom-1/2" : "top-0 bottom-1/2"
                    }`}
                    aria-hidden
                  />
                  {!isLast ? (
                    <div
                      className="absolute left-1/2 top-1/2 -bottom-8 w-px -translate-x-1/2 border-l border-dashed border-content-100"
                      aria-hidden
                    />
                  ) : null}
                  <div className="relative flex size-10 items-center justify-center rounded-full bg-background-900">
                    <HugeiconsIcon
                      icon={ACTIVITY_ICON[entry.kind]}
                      size={18}
                      className="text-content-400"
                    />
                  </div>
                </div>

                <div className="min-w-0 self-center">
                  <p className="font-bold">{entry.label}</p>
                  {entry.detail ? (
                    <p className="mt-1 text-content-400">{entry.detail}</p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export const EmployerVerificationDetails = ({
  employerVerification
}: {
  employerVerification: EmployerVerification;
}) => {
  const { supervisor, decline, answers, activity, mismatch_count } =
    employerVerification;
  const { applicationStatus, refetch } = useCombinedIncomeContext();

  const [confirmingResend, setConfirmingResend] = useState(false);
  const [editing, setEditing] = useState(false);

  const resend = useMutation({
    mutationKey: ["EMPLOYER_VERIFICATION_RESEND", employerVerification.token],
    mutationFn: () =>
      landlordSurveyApi.resendSurvey(employerVerification.token),
    onSuccess: () => {
      toast.success("Employer verification was re-sent");
      setConfirmingResend(false);
      refetch();
    },
    onError: (error) =>
      toast.error(
        `Could not resend the employer verification: ${getErrorMessage(error)}`
      )
  });

  const isDeclined = employerVerification.status === "declined";
  const isOnHold = applicationStatus === ApplicationStatus.OnHold;
  const isOpen = OPEN_STATUSES.includes(employerVerification.status);
  const actionsDisabled = isDeclined || isOnHold;
  // Resending a finished verification re-texts a third party who has already answered,
  // and on a canceled or failed survey the API refuses it outright — so Resend and Edit
  // are blocked in exactly the same states.
  const actionsBlocked = actionsDisabled || !isOpen;
  const actionTooltip = blockedReason({
    isDeclined,
    isOnHold,
    status: employerVerification.status
  });

  return (
    <div className="w-full">
      {mismatch_count > 0 ? (
        <Banner
          variant="warning"
          textTheme="base"
          className="mt-4 rounded-lg"
          body={
            mismatch_count === 1
              ? "1 answer does not match what the applicant reported. The answers below are highlighted."
              : `${mismatch_count} answers do not match what the applicant reported. The answers below are highlighted.`
          }
          HugeIcon={
            <HugeiconsIcon
              icon={AlertCircleIcon}
              className="shrink-0 text-warning-500"
              size="20"
            />
          }
        />
      ) : null}

      <div className="mt-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-bold text-content-1000">
            Verification details
          </h2>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            disabled={actionsBlocked}
            loading={resend.isPending}
            tooltip={actionTooltip}
            tooltipDelay={250}
            onClick={() => setConfirmingResend(true)}
            HugeIcon={MailSend02Icon}
          >
            Resend
          </Button>
        </div>
        <div className="flex items-center justify-between gap-2 border-b border-alpha-80 pb-3 pt-8">
          <p className="text-sm font-bold text-content-1000">Supervisor</p>
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            disabled={actionsBlocked}
            tooltip={actionTooltip}
            tooltipDelay={250}
            onClick={() => setEditing(true)}
          >
            Edit details
          </Button>
        </div>
        <div className="space-y-2 pt-4">
          <DetailRow
            label="Company"
            value={employerVerification.company_name}
          />
          <DetailRow label="Name" value={supervisorName(supervisor)} />
          <DetailRow label="Email" value={supervisor.email} />
          <DetailRow
            label="Phone"
            value={supervisor.phone ? formatPhone(supervisor.phone) : undefined}
          />
          <DetailRow
            label="Decline reason"
            value={employerDeclineReasonLabel(decline?.reason)}
          />
          <DetailRow label="Decline details" value={decline?.details} />
        </div>
      </div>

      {answers.length > 0 || isOpen ? (
        <div className="mt-8">
          <div className="flex items-center justify-between gap-2 border-b border-alpha-80 pb-3">
            <h2 className="text-sm font-bold text-content-1000">
              Employer&apos;s answers
            </h2>
            {/*
              Lets a PM record what a supervisor gave them over the phone, the same way
              housing history's "Fill out survey" does. The token page accepts a partner
              user acting on the recipient's behalf.
            */}
            {isOpen ? (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`/land-x/${employerVerification.token}?as_partner_user=true`}
              >
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.SM}
                  type="button"
                  className="!text-sm"
                >
                  Fill out for employer
                </Button>
              </a>
            ) : null}
          </div>
          <div className="space-y-2 pt-4">
            {answers.map((answer) => (
              <AnswerRow key={answer.question_id} answer={answer} />
            ))}
          </div>
        </div>
      ) : null}

      <ActivityTimeline activity={activity} />

      <ConfirmDialog
        opened={confirmingResend}
        onClose={() => setConfirmingResend(false)}
        onConfirm={() => resend.mutate()}
        variant="primary"
        icon={{ icon: MailSend02Icon }}
        title="Resend verification request?"
        description={
          supervisor.email || supervisor.phone
            ? `We will send the request to ${[supervisor.email, supervisor.phone && formatPhone(supervisor.phone)].filter(Boolean).join(" and ")} again.`
            : "We will send the request to the supervisor again."
        }
        confirmButton={{ label: "Resend", loading: resend.isPending }}
      />

      {editing ? (
        <EditSupervisorDetailsModal
          employerVerification={employerVerification}
          onClose={() => setEditing(false)}
          onSaved={refetch}
        />
      ) : null}
    </div>
  );
};
